// ─────────────────────────────────────────────────────────────────────────────
// AR PIPELINE — Full 7-Phase Orchestrator
//
// Phase 1: Camera → Three.js render loop → skeleton overlay
// Phase 2: MediaPipe Pose 33-joint tracking + One-Euro smoothing
// Phase 3: Shader-warped garment plane (2D PNG mode)
// Phase 5-6: GLB loader + bone rigging (3D garment mode)
// Phase 7: Segmentation occlusion + depth-refined mask
// Phase 13-15: Monocular depth + stencil layering
//
// NEW PHASES (Hybrid Workflow):
// Phase 8: Face detection + facial landmarks (MediaPipe FaceLandmarker)
// Phase 9: Face/head segmentation mask (MediaPipe Selfie Segmenter)
// Phase 10: Clothing segmentation (CEM placeholder)
// Phase 11: Video frame buffer + BFS (Best Frame Selection)
//
// Degradation tiers (auto-detected):
//   Tier 1 — Pose + Segmentation + WebGL + Depth
//   Tier 2 — Pose + WebGL (no segmentation)
//   Tier 3 — Heuristic placement (no MediaPipe)
// ─────────────────────────────────────────────────────────────────────────────
import { ARRenderer } from "./renderer";
import * as THREE from "three";
import {
  getPoseLandmarker,
  extractTorsoGeometry,
  type NormalizedLandmark,
} from "./pose";
import { getSegmenter, maskToImageData } from "./segmenter";
import { OneEuroLandmarkSmoother } from "./smoother";
import { SkeletonOverlay } from "./skeleton-overlay";
import { refineMaskWithDepth } from "./depth";
import {
  loadGLBGarment,
  rigGarmentFromPose,
  type LoadedGarment,
} from "./glb-loader";
import * as poseModule from "./pose";
import * as segModule from "./segmenter";

// NEW: Hybrid workflow imports
import { getFaceLandmarker, detectFaces, getFaceBoundingBox, drawFaceDebugOverlay, FACE_IDX } from "./face";
import { getFaceSegmenter } from "./face-segmenter";
import { getClothingSegmenter, segmentClothing } from "./clothing-segmenter";
import { VideoFrameBuffer } from "./frame-buffer";

// Cloth physics
import { PBDClothSim, FABRIC_PRESETS, type ClothParams } from "./cloth-physics";
import { loadAmmo, AmmoClothSim } from "./ammo-cloth";

// ── Unified pipeline (device-adaptive 2D/3D routing) ─────────────────────────
import { UnifiedPipeline } from "./unified-pipeline";
import { loadGarment, loadCatalog, getGarmentById } from "./garment-loader";
import type { GarmentSchema } from "./garment-schema";

// ── Vision AI models ──────────────────────────────────────────────────────────
import {
  getHolisticLandmarker,
  detectHolistic,
  isHolisticReady,
} from "./holistic";
import { computeArmMask } from "./body-segmentation";
import { loadDepthModel, estimateDepth, isDepthModelReady } from "./depth-model";
import {
  runSAM2,
  captureFrameBase64,
  buildPromptPoints,
  getCachedSAM2Mask,
  setCachedSAM2Mask,
  clearSAM2Cache,
} from "./sam2-client";

export type ARTier = 1 | 2 | 3;
export type ARStatus = "initializing" | "ready" | "error";

export interface ARPipelineOptions {
  canvas: HTMLCanvasElement;
  video: HTMLVideoElement;
  /** Optional 2D canvas overlay for skeleton visualization */
  skeletonCanvas?: HTMLCanvasElement;
  onTierChange?: (tier: ARTier) => void;
  onStatusChange?: (status: ARStatus) => void;
  onFpsUpdate?: (fps: number) => void;
}

export class ARPipeline {
  private renderer: ARRenderer;
  private skeletonOverlay: SkeletonOverlay | null = null;
  private smoother: OneEuroLandmarkSmoother;
  private rafId = 0;
  private running = false;
  private tier: ARTier = 3;
  private lastVideoTime = -1;
  private garment: LoadedGarment | null = null;

  // NEW: Face attachment for accessories
  private faceGarment: {
    garment: LoadedGarment;
    attachmentPoint: 'eyes' | 'head' | 'nose' | 'ears';
    offset: THREE.Vector3;
  } | null = null;

  // Face debug canvas (passed via opts.skeletonCanvas)
  private skeletonCanvas: HTMLCanvasElement | null = null;

  // Cloth simulation — PBD (fast) or ammo.js (HD)
  private clothSim: PBDClothSim | AmmoClothSim | null = null;
  private clothEnabled = false;
  private clothGarmentUrl = "";
  private lastClothStep = 0;

  // ── Unified pipeline — device-adaptive 2D/3D routing ─────────────────────
  private unifiedPipeline: UnifiedPipeline | null = null;

  // ── Vision AI model state ─────────────────────────────────────────────────
  private holisticReady    = false;
  private depthModelReady  = false;
  // SAM2: run once per session on first high-confidence frame
  private sam2Requested    = false;
  private sam2ApiToken     = "";  // set via setSAM2Token()

  // NEW: Hybrid workflow components
  private frameBuffer: VideoFrameBuffer;
  private faceLandmarkerReady = false;
  private faceSegmenterReady = false;
  private clothingSegmenterReady = false;

  // FPS tracking
  private frameCount = 0;
  private fpsTimer = 0;
  private currentFps = 0;

  // Settings (synced from UI)
  private facingMode: "user" | "environment" = "user";
  private isBottom = false;
  private showSkeleton = false;

  // Callbacks
  private onTierChange?: (tier: ARTier) => void;
  private onStatusChange?: (status: ARStatus) => void;
  private onFpsUpdate?: (fps: number) => void;

  constructor(opts: ARPipelineOptions) {
    this.renderer = new ARRenderer(opts.canvas, opts.video);
    this.smoother = new OneEuroLandmarkSmoother(
      33,       // 33 MediaPipe body landmarks
      1.0,      // minCutoff: 1Hz = smooth stillness
      0.007     // beta: low value = less lag on movement
    );

    // NEW: Initialize frame buffer for BFS
    this.frameBuffer = new VideoFrameBuffer({
      maxFrames: 30,
      scoringWeights: { poseWeight: 0.6, visibilityWeight: 0.4 }
    });

    if (opts.skeletonCanvas) {
      this.skeletonCanvas = opts.skeletonCanvas;
      this.skeletonOverlay = new SkeletonOverlay({
        canvas: opts.skeletonCanvas,
        mirror: true,
        showJoints: true,
        showBones: true,
        minVisibility: 0.4,
        jointRadius: 5,
        lineWidth: 2,
      });
    }

    this.onTierChange = opts.onTierChange;
    this.onStatusChange = opts.onStatusChange;

    // Initialize unified pipeline (device tier auto-detected)
    const threeRenderer = (this.renderer as unknown as { renderer: THREE.WebGLRenderer }).renderer;
    const threeScene    = (this.renderer as unknown as { scene: THREE.Scene }).scene;
    const threeCamera   = (this.renderer as unknown as { camera: THREE.Camera }).camera;
    if (threeRenderer && threeScene && threeCamera) {
      this.unifiedPipeline = new UnifiedPipeline({
        canvas:        opts.canvas,
        threeRenderer,
        threeScene,
        threeCamera,
        isMirrored:    true,
        skeletalWeight: 0.7,
      });
      console.log(`[Pipeline] UnifiedPipeline mode: ${this.unifiedPipeline.mode}`);
    }

    // Preload catalog in background
    loadCatalog("/garments/catalog.json").catch(() => {
      console.warn("[Pipeline] Catalog not found — using legacy garment system");
    });
    this.onFpsUpdate = opts.onFpsUpdate;
  }

  // ── Boot — start tier 3 immediately, upgrade in background ───────────────
  async init() {
    this.onStatusChange?.("initializing");

    // Immediate render at tier 3
    this.setTier(3);
    this.startLoop();

    // Async upgrade path
    try {
      const landmarker = await getPoseLandmarker();
      if (landmarker) {
        this.setTier(2);
        const segmenter = await getSegmenter();
        if (segmenter) this.setTier(1);
      }

      // ── Holistic (unified pose+face+hands — replaces separate landmarkers) ──
      const holistic = await getHolisticLandmarker();
      if (holistic) {
        this.holisticReady = true;
        console.log("✅ MediaPipe Holistic ready (pose+face+hands)");
      } else {
        // Fall back to separate face landmarker for accessories
        const faceLandmarker = await getFaceLandmarker();
        if (faceLandmarker) {
          this.faceLandmarkerReady = true;
          console.log("✅ Face detection ready (fallback)");
        }
      }

      // ── Depth Anything V2 (ONNX, throttled to ~5fps internally) ─────────
      const depthOk = await loadDepthModel();
      if (depthOk) {
        this.depthModelReady = true;
        console.log("✅ Depth Anything V2 ready");
      }

      // ── Legacy segmenters ────────────────────────────────────────────────
      const faceSegmenter = await getFaceSegmenter();
      if (faceSegmenter) {
        this.faceSegmenterReady = true;
        console.log("✅ Face segmentation ready");
      }

      const clothingSegmenter = await getClothingSegmenter();
      if (clothingSegmenter) {
        this.clothingSegmenterReady = true;
        console.log("✅ Clothing segmentation ready");
      }

    } catch {
      // Stay at current tier
    }

    this.onStatusChange?.("ready");
  }

  private setTier(t: ARTier) {
    if (t !== this.tier) {
      this.tier = t;
      this.onTierChange?.(t);
      // Reset smoother on tier change (avoids stale state)
      this.smoother.reset();
    }
  }

  // ── Render loop ───────────────────────────────────────────────────────────
  private startLoop() {
    this.running = true;
    const loop = (now: number) => {
      if (!this.running) return;
      // tick is async but we don't await it here — RAF fires independently
      // errors are caught inside tick()
      this.tick(now).catch(() => {}).finally(() => {
        if (this.running) this.rafId = requestAnimationFrame(loop);
      });
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private async tick(now: number) {
    // FPS tracking
    this.frameCount++;
    if (now - this.fpsTimer > 1000) {
      this.currentFps = Math.round(this.frameCount * 1000 / (now - this.fpsTimer));
      this.frameCount = 0;
      this.fpsTimer = now;
      this.onFpsUpdate?.(this.currentFps);
    }

    const video = this.getVideo();
    if (!video || video.readyState < 2) {
      this.renderer.render();
      return;
    }

    // Size canvas to SCREEN (not video) dimensions so aspect ratio is correct.
    // VideoTexture UV is adjusted separately for cover-fit.
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    {
      const canvas = this.renderer.getCanvas();
      // Use the canvas's CSS display size (accounts for mobile viewport changes)
      const sw = canvas.offsetWidth  || window.innerWidth;
      const sh = canvas.offsetHeight || window.innerHeight;
      if (canvas.width !== sw || canvas.height !== sh) {
        this.renderer.resize(sw, sh);
      }
      // Always re-sync UV cover-fit when video dims are known
      if (vw > 0 && vh > 0) {
        this.renderer.updateVideoAspect(vw, vh, sw, sh);
      }
    }

    const ts = video.currentTime;
    let smoothedLandmarks: NormalizedLandmark[] | null = null;

    // ── TIER 1 or 2: Pose detection (Holistic preferred, pose fallback) ──────
    if (this.tier <= 2) {
      try {
        if (ts !== this.lastVideoTime) {
          let rawLandmarks: NormalizedLandmark[] | null = null;
          let faceLandmarksThisFrame: Array<{ x: number; y: number; z: number }> | null = null;

          // ── A) MediaPipe Holistic (pose + face + hands in one pass) ────────
          if (this.holisticReady && isHolisticReady()) {
            const holResult = detectHolistic(video, performance.now());
            if (holResult) {
              rawLandmarks = holResult.poseLandmarks as NormalizedLandmark[];
              faceLandmarksThisFrame = holResult.faceLandmarks;
            }
          }

          // ── B) Fallback: legacy PoseLandmarker ────────────────────────────
          if (!rawLandmarks) {
            const landmarker = poseModule._landmarker;
            if (landmarker) {
              const result = landmarker.detectForVideo(video, performance.now());
              rawLandmarks = (result.landmarks?.[0] as NormalizedLandmark[] | undefined) ?? null;
            }
          }

          if (rawLandmarks && rawLandmarks.length > 0) {
            // One-Euro smoothing
            smoothedLandmarks = this.smoother.smooth(rawLandmarks, performance.now());
            const isMirrored  = this.facingMode === "user";

            // ── Unified pipeline update (2D or 3D adaptive) ────────────────
            if (this.unifiedPipeline) {
              this.unifiedPipeline.update(smoothedLandmarks, performance.now());
            } else if (this.garment) {
              rigGarmentFromPose(this.garment, {
                landmarks: smoothedLandmarks,
                canvasW: vw,
                canvasH: vh,
                isMirrored,
              });
            }

            // ── Cloth simulation step ──────────────────────────────────────
            if (this.clothEnabled && this.clothSim) {
              const now2 = performance.now();
              const dt   = Math.min((now2 - this.lastClothStep) / 1000, 1 / 30);
              this.lastClothStep = now2;
              if (this.clothSim instanceof PBDClothSim) {
                this.clothSim.pinToSkeleton(smoothedLandmarks, isMirrored);
                this.clothSim.applyBodyCollision(smoothedLandmarks, isMirrored);
                this.clothSim.step(dt);
              } else {
                this.clothSim.pinToSkeleton(smoothedLandmarks, isMirrored);
                this.clothSim.step(dt);
              }
            }

            // ── Drive WebGL garment position ───────────────────────────────
            if (!this.clothEnabled) {
              const torso = extractTorsoGeometry(smoothedLandmarks);
              if (torso && torso.confidence > 0.4) {
                this.renderer.updateFromPose(torso, isMirrored);
              } else {
                this.renderer.setHeuristicPosition(this.isBottom);
              }
            }

            // ── Skeleton overlay ───────────────────────────────────────────
            if (this.showSkeleton && this.skeletonOverlay) {
              this.skeletonOverlay.draw(smoothedLandmarks, vw, vh);
            } else {
              this.skeletonOverlay?.clear();
            }

            // ── Arm occlusion mask (landmark-polygon, every frame) ─────────
            // Punches arm-shaped holes in garment so arms appear IN FRONT
            try {
              const armMask = computeArmMask(smoothedLandmarks, vw, vh, isMirrored);
              this.renderer.updateArmOcclusionMask(armMask.data, vw, vh);
            } catch { /* non-critical */ }

            // ── Depth Anything V2 (throttled internally to ~5fps) ─────────
            if (this.depthModelReady && isDepthModelReady()) {
              try {
                const depthMap = await estimateDepth(video);
                if (depthMap) {
                  this.renderer.setLatestDepthMap(depthMap);
                }
              } catch { /* non-critical */ }
            }

            // ── SAM2 — run once per session on first confident frame ───────
            if (!this.sam2Requested && !getCachedSAM2Mask()) {
              // Require decent landmark confidence before triggering SAM2
              const avgVis = smoothedLandmarks.slice(11, 25)
                .reduce((s, lm) => s + (lm.visibility ?? 1), 0) / 14;
              if (avgVis > 0.6) {
                this.sam2Requested = true;
                // Fire and forget — mask will be cached when ready
                const { base64, width, height } = captureFrameBase64(video);
                const points = buildPromptPoints(smoothedLandmarks, width, height, isMirrored);
                runSAM2({ imageBase64: base64, width, height, points }, this.sam2ApiToken)
                  .then(mask => {
                    if (mask) {
                      const sessionId = `${Date.now()}`;
                      setCachedSAM2Mask(mask, sessionId);
                      void sessionId; // reserved for session resumption
                      // Feed SAM2 person mask into renderer for high-quality occlusion
                      this.renderer.updateSAM2PersonMask(mask);
                      console.log("[Pipeline] SAM2 mask cached — provider:", (mask as unknown as { provider?: string }).provider ?? "sam2");
                    }
                  })
                  .catch(() => {
                    // Allow retry next session
                    this.sam2Requested = false;
                  });
              }
            }

            // ── Face accessories (holistic face landmarks or fallback) ──────
            const faceData = faceLandmarksThisFrame;
            if (faceData && faceData.length > 0) {
              if (this.faceGarment) {
                this.positionFaceGarment(faceData as unknown as never[], vw, vh);
              }
              if (this.skeletonCanvas) {
                // detectFaces result not available here; skip debug overlay when using holistic
              }
            } else if (this.faceLandmarkerReady && !this.holisticReady) {
              try {
                const faceResult = await detectFaces(video);
                if (faceResult?.faceLandmarks?.[0]) {
                  const fl = faceResult.faceLandmarks[0];
                  if (this.faceGarment) this.positionFaceGarment(fl, vw, vh);
                  if (this.skeletonCanvas) drawFaceDebugOverlay(this.skeletonCanvas, faceResult, vw, vh);
                  else getFaceBoundingBox(fl);
                }
              } catch { /* non-critical */ }
            }

            // ── Frame buffering for BFS (Best Frame Selection) ────────────
            try {
              await this.frameBuffer.addFrame(video, smoothedLandmarks);
              const bestFrame = this.frameBuffer.getBestFrame();
              if (bestFrame && this.clothingSegmenterReady) {
                const clothingResult = await segmentClothing(bestFrame.imageData as unknown as HTMLVideoElement);
                void clothingResult; // reserved for future garment fitting
              }
            } catch { /* non-critical */ }

          } else {
            this.renderer.setHeuristicPosition(this.isBottom);
            this.skeletonOverlay?.clear();
          }
        }
      } catch {
        this.renderer.setHeuristicPosition(this.isBottom);
      }
    } else {
      // Tier 3 heuristic
      this.renderer.setHeuristicPosition(this.isBottom);
    }

    // ── TIER 1: Segmentation + Depth-refined occlusion ──────────────────────
    if (this.tier === 1 && ts !== this.lastVideoTime) {
      const segmenter = segModule._segmenter;
      if (segmenter) {
        try {
          const result = segmenter.segmentForVideo(video, performance.now());
          const mask = result.confidenceMasks?.[0];
          if (mask) {
            let float32 = mask.getAsFloat32Array();

            // Phase 13-15: Refine mask with depth data from landmarks
            if (smoothedLandmarks) {
              float32 = refineMaskWithDepth(
                float32,
                smoothedLandmarks,
                video.videoWidth,
                video.videoHeight,
                this.facingMode === "user"
              );
            }

            const imgData = maskToImageData(float32, video.videoWidth, video.videoHeight);
            this.renderer.updateOcclusionMask(imgData);
            mask.close();
          }
        } catch { /* non-critical */ }
      }
    }

    if (ts !== this.lastVideoTime) {
      this.lastVideoTime = ts;
    }

    this.renderer.render();
  }

  private getVideo(): HTMLVideoElement | null {
    return (this.renderer as unknown as {
      videoTex: { image: HTMLVideoElement }
    }).videoTex?.image ?? null;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Enable/disable cloth simulation.
   * preset: 'silk' | 'cotton' | 'denim' | 'leather' | 'knit'
   * hd: true = ammo.js btSoftBody, false = PBD (fast fallback)
   */
  async enableCloth(enabled: boolean, preset = "cotton", hd = false) {
    this.clothEnabled = enabled;

    if (!enabled) {
      this.clothSim?.dispose?.();
      this.clothSim = null;
      this.renderer.removeClothMesh();
      return;
    }

    // Dispose previous
    this.clothSim?.dispose?.();
    this.clothSim = null;

    const fabricParams: Partial<ClothParams> = FABRIC_PRESETS[preset] ?? FABRIC_PRESETS["cotton"];

    if (hd) {
      // Try ammo.js btSoftBody
      const Ammo = await loadAmmo();
      if (Ammo) {
        const sim = new AmmoClothSim(Ammo);
        sim.setFabric({
          stiffness: fabricParams.stiffness,
          damping:   fabricParams.damping,
          mass:      fabricParams.mass,
        });
        this.clothSim = sim;
        if (this.clothGarmentUrl) {
          this.renderer.setClothMesh(sim.geometry, this.clothGarmentUrl);
        }
        this.lastClothStep = performance.now();
        console.log("[Pipeline] Cloth: ammo.js btSoftBody ✓");
        return;
      }
      // fallthrough to PBD
    }

    // PBD fallback (fast, no WASM required)
    const sim = new PBDClothSim(16, 24, 0.6, 0.8, fabricParams);
    this.clothSim = sim;
    if (this.clothGarmentUrl) {
      this.renderer.setClothMesh(sim.geometry, this.clothGarmentUrl);
    }
    this.lastClothStep = performance.now();
    console.log("[Pipeline] Cloth: PBD ✓");
  }

  // ── NEW: Load garment via unified schema (preferred entry point) ─────────
  async setGarmentFromSchema(schemaOrId: GarmentSchema | string) {
    const schema = typeof schemaOrId === "string"
      ? getGarmentById(schemaOrId)
      : schemaOrId;

    if (!schema) {
      console.warn(`[Pipeline] Garment schema not found: ${schemaOrId}`);
      return;
    }

    const mode = this.unifiedPipeline?.mode ?? "2d";
    const loaded = await loadGarment(schema, mode);

    if (this.unifiedPipeline) {
      // Route through unified pipeline (handles 2D/3D switching internally)
      await this.unifiedPipeline.setGarment(loaded);
      // Disable legacy garment system when unified is active
      this.garment = null;
      this.renderer.removeGarmentScene();
      if (mode === "2d") {
        // Pass image to legacy renderer for its heuristic fallback
        if (loaded.image) this.renderer.setGarmentImage(schema.assets.image ?? "");
      }
    } else {
      // Fallback to legacy system
      if (schema.assets.glb) {
        await this.setGarmentGLB(schema.assets.glb);
      } else if (schema.assets.image) {
        this.setGarmentImage(schema.assets.image);
      }
    }

    console.log(`[Pipeline] Garment loaded: ${schema.name} (mode=${mode})`);
  }

  setGarmentImage(url: string) {
    this.clothGarmentUrl = url;
    this.garment = null;
    this.renderer.removeGarmentScene();
    this.renderer.setGarmentImage(url);
    // If cloth is live, update its texture too
    if (this.clothEnabled && this.clothSim) {
      this.renderer.setClothMesh(this.clothSim.geometry, url);
    }
  }

  async setGarmentGLB(url: string) {
    if (this.garment) {
      this.renderer.removeGarmentScene();
      this.garment = null;
    }

    this.garment = await loadGLBGarment(url);
    this.renderer.addGarmentScene(this.garment.scene);
    this.renderer.setUseGLBGarment(true);
  }

  /**
   * Attach a garment to the face using facial landmarks
   * Used for accessories like glasses, hats, jewelry
   */
  async setGarmentFaceAttachment(url: string, attachmentPoint: 'eyes' | 'head' | 'nose' | 'ears' = 'head') {
    try {
      const faceGarment = await loadGLBGarment(url);

      // Store face attachment info
      this.faceGarment = {
        garment: faceGarment,
        attachmentPoint,
        offset: this.getFaceAttachmentOffset(attachmentPoint)
      };

      // Add to renderer scene
      this.renderer.addGarmentScene(faceGarment.scene);

      console.log(`✅ Face garment loaded (${attachmentPoint}):`, faceGarment.type);
    } catch (err) {
      console.error("❌ Failed to load face garment:", err);
    }
  }

  private getFaceAttachmentOffset(point: 'eyes' | 'head' | 'nose' | 'ears'): THREE.Vector3 {
    // Face landmark indices for positioning
    const offsets = {
      eyes: new THREE.Vector3(0, -0.05, 0.1),    // Slightly above eye level
      head: new THREE.Vector3(0, 0.1, 0),         // Top of head
      nose: new THREE.Vector3(0, 0, 0.05),        // Nose bridge
      ears: new THREE.Vector3(0.08, 0, 0)         // Ear level
    };
    return offsets[point];
  }

  private positionFaceGarment(faceLandmarks: any[], _videoWidth: number, _videoHeight: number) {
    if (!this.faceGarment) return;

    const { garment, attachmentPoint, offset } = this.faceGarment;

    // Get the reference landmark based on attachment point
    let referenceLandmark;
    switch (attachmentPoint) {
      case 'eyes':
        // Use midpoint between eyes
        const leftEye = faceLandmarks[FACE_IDX.LEFT_EYE];
        const rightEye = faceLandmarks[FACE_IDX.RIGHT_EYE];
        referenceLandmark = {
          x: (leftEye.x + rightEye.x) / 2,
          y: (leftEye.y + rightEye.y) / 2,
          z: (leftEye.z + rightEye.z) / 2
        };
        break;
      case 'head':
        referenceLandmark = faceLandmarks[FACE_IDX.NOSE_TIP];
        break;
      case 'nose':
        referenceLandmark = faceLandmarks[FACE_IDX.NOSE_TIP];
        break;
      case 'ears':
        // Use midpoint between ears
        const leftEar = faceLandmarks[FACE_IDX.LEFT_EAR];
        const rightEar = faceLandmarks[FACE_IDX.RIGHT_EAR];
        referenceLandmark = {
          x: (leftEar.x + rightEar.x) / 2,
          y: (leftEar.y + rightEar.y) / 2,
          z: (leftEar.z + rightEar.z) / 2
        };
        break;
    }

    if (referenceLandmark) {
      // Convert normalized coordinates to world space
      const worldX = (referenceLandmark.x - 0.5) * 2; // -1 to 1 range
      const worldY = -(referenceLandmark.y - 0.5) * 2; // -1 to 1 range (flip Y)
      const worldZ = referenceLandmark.z * 2; // 0 to 2 range (depth)

      // Apply offset
      garment.scene.position.set(
        worldX + offset.x,
        worldY + offset.y,
        worldZ + offset.z
      );

      // Scale based on face size (rough estimate)
      const faceWidth = Math.abs(faceLandmarks[FACE_IDX.LEFT_EAR].x - faceLandmarks[FACE_IDX.RIGHT_EAR].x);
      const scale = Math.max(0.1, faceWidth * 3); // Scale factor based on face width
      garment.scene.scale.setScalar(scale);
    }
  }

  clearGarment() {
    if (this.garment) {
      this.renderer.removeGarmentScene();
      this.garment = null;
    }
    if (this.faceGarment) {
      this.renderer.removeGarmentScene();
      this.faceGarment = null;
    }
    this.renderer.clearGarment();
  }

  setSettings(opts: {
    opacity?: number;
    brightness?: number;
    contrast?: number;
    facingMode?: "user" | "environment";
    isBottom?: boolean;
    showSkeleton?: boolean;
  }) {
    if (opts.opacity !== undefined)     this.renderer.setOpacity(opts.opacity);
    if (opts.brightness !== undefined)  this.renderer.setBrightness(opts.brightness);
    if (opts.contrast !== undefined)    this.renderer.setContrast(opts.contrast);
    if (opts.facingMode !== undefined) {
      this.facingMode = opts.facingMode;
      this.renderer.setMirror(opts.facingMode === "user");
      this.skeletonOverlay?.setOptions({ mirror: opts.facingMode === "user" });
      this.unifiedPipeline?.setMirrored(opts.facingMode === "user");
    }
    if (opts.isBottom !== undefined)    this.isBottom = opts.isBottom;
    if (opts.showSkeleton !== undefined) this.showSkeleton = opts.showSkeleton;
  }

  snapshot(): string {
    return this.renderer.snapshot();
  }

  getFps(): number { return this.currentFps; }
  getTier(): ARTier { return this.tier; }

  // NEW: Hybrid workflow access methods
  getBestFrame() {
    return this.frameBuffer.getBestFrame();
  }

  getAllFrames() {
    return this.frameBuffer.getAllFrames();
  }

  clearFrameBuffer() {
    this.frameBuffer.clear();
  }

  isFaceDetectionReady(): boolean {
    return this.faceLandmarkerReady;
  }

  isFaceSegmentationReady(): boolean {
    return this.faceSegmenterReady;
  }

  isClothingSegmentationReady(): boolean {
    return this.clothingSegmenterReady;
  }

  /** Set Replicate API token for SAM2 cloud fallback */
  setSAM2Token(token: string) {
    this.sam2ApiToken = token;
  }

  /** Force a new SAM2 segmentation on the next confident frame */
  resetSAM2() {
    this.sam2Requested = false;
    clearSAM2Cache();
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  dispose() {
    this.stop();
    this.unifiedPipeline?.dispose();
    this.unifiedPipeline = null;
    this.clothSim?.dispose?.();
    this.clothSim = null;
    this.renderer.dispose();
    this.smoother.reset();
    this.skeletonOverlay?.clear();
    clearSAM2Cache();
  }

  // ── Unified pipeline accessors ─────────────────────────────────────────────
  getUnifiedMode() {
    return this.unifiedPipeline?.mode ?? null;
  }

  // Expose loadCatalog / getGarmentById for TryOn UI
  async loadGarmentCatalog() {
    return loadCatalog("/garments/catalog.json");
  }
}
